import React, { useEffect, useState } from "react";
import Navbar from "../../components/Navbar";
import { motion } from "framer-motion";
import {
  ChevronUp,
  ChevronDown,
  Search,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  Users,
  UserPlus,
  UserCheck,
  UserX,
  TrendingUp,
  Target,
  Eye,
  Calendar,
  X,
  Download,
  Info,
} from "lucide-react";
import ViewSubscribersModal from "../../components/Subscribers/ViewSubscribersModal";
import DeactivateModal from "../../components/Brands/DeactivateModal";
import Loader from "../../components/Loader";
import subscriberService from "../../requests/subscribers/SubscriberService";
import useSubscribers from "../../hooks/useSubscribers";

const Subscribers = () => {
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [deactivateModal, setDeactivateModal] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [copyAnimation, setCopyAnimation] = useState({
    email: null,
    phone: null,
  });
  const [dropdownPosition, setDropdownPosition] = useState({});
  const [exportModal, setExportModal] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    exportType: "all",
    dateFrom: "",
    dateTo: "",
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [subscriberStats, setSubscriberStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    newToday: 0,
    growthVelocity: 0,
    weeklyGrowth: 0,
    dailyGrowth: 0,
    overallGrowth: 0,
    engagementRate: 0,
  });
  const [totalSubscribers, setTotalSubscribers] = useState(0);

  // Use the custom hook for efficient data management
  const {
    subscribers,
    loading,
    error,
    pagination,
    filters,
    debouncedSearch,
    changePage,
    changeItemsPerPage,
    updateSort,
    updateDateFilter,
    clearDateFilter,
    // clearAllFilters,
    updateChannelFilter,
  } = useSubscribers(totalSubscribers);

  // Fetch subscriber statistics from API
  const fetchSubscriberStats = async () => {
    try {
      const response = await subscriberService.getSubscriberStats();

      if (response.status_code === "000") {
        setTotalSubscribers(response.stats.total);
        setSubscriberStats(
          response.stats || {
            total: 0,
            active: 0,
            inactive: 0,
            newToday: 0,
            growthVelocity: 0,
            weeklyGrowth: 0,
            dailyGrowth: 0,
            overallGrowth: 0,
            engagementRate: 0,
          }
        );
      } else {
        console.error(
          "Error fetching subscriber stats:",
          response.data?.data?.reason
        );
      }
    } catch (error) {
      console.error("Error fetching subscriber stats:", error);
    }
  };

  useEffect(() => {
    fetchSubscriberStats();
  }, []);

  // Handle clicking outside date filter dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDateFilter && !event.target.closest(".date-filter-container")) {
        setShowDateFilter(false);
      }

      // Close dropdown when clicking outside
      if (dropdownOpen && !event.target.closest(".action-dropdown")) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDateFilter, dropdownOpen]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  const shortMonthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const currentDate = new Date();
  const currentMonthIndex = currentDate.getMonth();
  const previousMonthIndex = (currentMonthIndex - 1 + 12) % 12;

  const currentMonthShort = shortMonthNames[currentMonthIndex];
  const previousMonthShort = shortMonthNames[previousMonthIndex];

  //* Stat cards data using API stats
  const statCards = [
    {
      title: "Total",
      value: subscriberStats.total,
      change: `${subscriberStats.overallGrowth >= 0 ? "+" : ""}${
        subscriberStats.overallGrowth
      }%`,
      trend: subscriberStats.overallGrowth >= 0 ? "up" : "down",
      color: "blue",
      icon: <Users className="w-6 h-6" />,
      subtitle: "All time",
      bgGradient: "from-blue-500/10 to-indigo-500/10",
      borderGradient: "from-blue-500/20 to-indigo-500/20",
    },
    {
      title: "Active",
      value: subscriberStats.active,
      change: `${subscriberStats.engagementRate}%`,
      trend: "up",
      color: "green",
      icon: <UserCheck className="w-6 h-6" />,
      subtitle: "Currently Active",
      bgGradient: "from-green-500/10 to-emerald-500/10",
      borderGradient: "from-green-500/20 to-emerald-500/20",
    },
    {
      title: "Inactive",
      value: subscriberStats.inactive,
      change: `${100 - subscriberStats.engagementRate}%`,
      trend: "down",
      color: "red",
      icon: <UserX className="w-6 h-6" />,
      subtitle: "Needs attention",
      bgGradient: "from-red-500/10 to-rose-500/10",
      borderGradient: "from-red-500/20 to-rose-500/20",
    },
    {
      title: "New Today",
      value: subscriberStats.newToday,
      change: `${subscriberStats.dailyGrowth >= 0 ? "+" : ""}${
        subscriberStats.dailyGrowth
      }%`,
      trend: subscriberStats.dailyGrowth >= 0 ? "up" : "down",
      color: "emerald",
      icon: <UserPlus className="w-6 h-6" />,
      subtitle: "New Subscribers Today",
      bgGradient: "from-emerald-500/10 to-green-500/10",
      borderGradient: "from-emerald-500/20 to-green-500/20",
    },
    {
      title: `${currentMonthShort} ${currentDate.getFullYear()} Subscribers`,
      value: `${subscriberStats.thisMonth}`,
      change: `${subscriberStats.weeklyGrowth >= 0 ? "+" : ""}${
        subscriberStats.weeklyGrowth
      }%`,
      trend: subscriberStats.weeklyGrowth >= 0 ? "up" : "down",
      color: "purple",
      icon: <Target className="w-6 h-6" />,
      subtitle: "Current Month",
      bgGradient: "from-purple-500/10 to-violet-500/10",
      borderGradient: "from-purple-500/20 to-violet-500/20",
    },
    {
      title: "Subscriber Growth",
      value: `${subscriberStats.growthVelocity}%`,
      change: `${previousMonthShort} ${currentDate.getFullYear()} to ${currentMonthShort} ${currentDate.getFullYear()}`,
      trend: subscriberStats.growthVelocity >= 0 ? "up" : "down",
      color: "orange",
      icon: <TrendingUp className="w-6 h-6" />,
      subtitle: "Previous to Current Month Growth",
      bgGradient: "from-orange-500/10 to-amber-500/10",
      borderGradient: "from-orange-500/20 to-amber-500/20",
    },
  ];

  const requestSort = (key) => {
    let direction = "desc";
    if (filters.sortBy === key && filters.sortOrder === "desc") {
      direction = "asc";
    }
    updateSort(key, direction);
  };

  const getSortIcon = (columnKey) => {
    return filters.sortBy === columnKey ? (
      filters.sortOrder === "asc" ? (
        <ChevronUp size={16} />
      ) : (
        <ChevronDown size={16} />
      )
    ) : (
      <div className="flex flex-col ml-2">
        <ChevronUp size={12} />
        <ChevronDown size={12} />
      </div>
    );
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    debouncedSearch(value);
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    changeItemsPerPage(newLimit);
  };

  // Date filtering handlers
  const handleDateFilterApply = (dateFrom, dateTo) => {
    updateDateFilter(dateFrom, dateTo);
    setShowDateFilter(false);
  };

  const handleDateFilterClear = () => {
    clearDateFilter();
    setShowDateFilter(false);
  };

  // Handle copy animation
  const handleCopy = (text, type, id) => {
    navigator.clipboard.writeText(text);

    // Set animation state
    setCopyAnimation((prev) => ({ ...prev, [type]: id }));

    // Reset animation after 1.5 seconds
    setTimeout(() => {
      setCopyAnimation((prev) => ({ ...prev, [type]: null }));
    }, 1500);
  };

  const handleDropdownToggle = (itemId, event) => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = 120; // Approximate height of dropdown

    // Determine if dropdown should appear above or below
    const shouldShowAbove =
      spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

    setDropdownPosition({
      position: "fixed",
      right: window.innerWidth - rect.right,
      ...(shouldShowAbove
        ? { bottom: window.innerHeight - rect.top + 8 }
        : { top: rect.bottom + 8 }),
    });

    setDropdownOpen(dropdownOpen === itemId ? null : itemId);
  };

  const handleExportClick = () => {
    setExportModal(true);
  };

  const handleExportTypeChange = (type) => {
    setExportFilters((prev) => ({
      ...prev,
      exportType: type,
      // Reset date filters when "all" is selected
      ...(type === "all" && { dateFrom: "", dateTo: "" }),
    }));
  };

  const handleExportDateChange = (field, value) => {
    setExportFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleExportSubmit = async () => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams();

      // Add export type
      if (exportFilters.exportType !== "all") {
        params.append("channel", exportFilters.exportType);
      }
      if (exportFilters.dateFrom) {
        params.append("date_from", exportFilters.dateFrom);
      }
      if (exportFilters.dateTo) {
        params.append("date_to", exportFilters.dateTo);
      }

      // Call the export API
      const response = await subscriberService.exportSubscribers(
        params.toString()
      );

      if (response.status_code === "000") {
        console.log("Export successful:", response);
        setExportModal(false);
        setExportFilters({
          exportType: "all",
          dateFrom: "",
          dateTo: "",
        });
      } else {
        console.error("Export failed:", response);
      }
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportCancel = () => {
    setExportModal(false);
    setExportFilters({
      exportType: "all",
      dateFrom: "",
      dateTo: "",
    });
  };

  const isExportDisabled = () => {
    return false;
  };

  // Check if any filters are active
  const hasActiveFilters =
    filters.search || filters.dateFrom || filters.dateTo || filters.channel;

  // Get the correct total to display in pagination
  const getDisplayTotal = () => {
    if (hasActiveFilters) {
      return pagination?.totalItems || 0;
    }
    return subscriberStats.total || 0;
  };

  // Format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Generate pagination numbers efficiently
  const getPaginationNumbers = () => {
    const totalPages = pagination?.totalPages || 1;
    const currentPage = pagination?.currentPage || 1;
    const numbers = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        numbers.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          numbers.push(i);
        }
        numbers.push("...");
        numbers.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        numbers.push(1);
        numbers.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          numbers.push(i);
        }
      } else {
        numbers.push(1);
        numbers.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          numbers.push(i);
        }
        numbers.push("...");
        numbers.push(totalPages);
      }
    }

    return numbers;
  };

  const userId = (user_id) => {
    return (
      <div className="flex items-center">
        <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-200 shadow-sm">
          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-2"></div>
          <span className="font-mono">#{user_id}</span>
        </div>
      </div>
    );
  };

  if (loading && subscribers.length === 0) {
    return <Loader />;
  }

  return (
    <div>
      <Navbar page="Subscribers" />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        {/* Stats Dashboard as Header */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="px-6 pt-8 pb-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {statCards.map((card, index) => (
              <motion.div
                key={card.title}
                variants={itemVariants}
                className={`
                  group relative bg-white rounded-2xl overflow-hidden shadow-lg
                  hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1
                  before:absolute before:inset-0 before:bg-gradient-to-br ${card.bgGradient}
                  after:absolute after:inset-px after:rounded-[15px] after:bg-gradient-to-br ${card.borderGradient}
                `}
              >
                <div className="relative p-6">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-6">
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-500">
                        {card.subtitle}
                      </p>
                      <h3 className="text-lg font-bold text-gray-900">
                        {card.title}
                      </h3>
                    </div>
                    <div
                      className={`
                        p-3 rounded-xl bg-gradient-to-br ${card.bgGradient}
                        border border-gradient-to-br ${card.borderGradient}
                        group-hover:scale-110 transition-transform duration-300
                      `}
                    >
                      <div className={`text-${card.color}-600`}>
                        {card.icon}
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-baseline gap-3">
                    <span className="text-3xl font-bold text-gray-900">
                      {card.value}
                    </span>
                    <div
                      className={`
                        flex items-center px-2 py-1 rounded-full text-sm font-medium
                        ${
                          card.trend === "up"
                            ? "text-emerald-700 bg-emerald-50"
                            : "text-rose-700 bg-rose-50"
                        }
                      `}
                    >
                      <span className="mr-1">
                        {card.trend === "up" ? "↑" : "↓"}
                      </span>
                      {card.change}
                    </div>
                  </div>

                  {/* Decorative background icon */}
                  <div className="absolute -right-6 -bottom-6 opacity-[0.03] transform rotate-12 group-hover:rotate-0 group-hover:scale-110 transition-transform duration-300">
                    <div className={`w-32 h-32 text-${card.color}-600`}>
                      {card.icon}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="px-6 pb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
            {/* Search and Filters Container */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-1">
              {/* Search Input */}
              <div className="relative w-full sm:w-72">
                <input
                  type="text"
                  placeholder="Search subscribers..."
                  defaultValue={filters.search}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-200 outline-none shadow-sm"
                />
                <Search
                  size={18}
                  className="absolute text-gray-400 transform -translate-y-1/2 left-3 top-1/2"
                />
              </div>

              {/* Date Filter Button */}
              <div className="relative date-filter-container w-full sm:w-auto">
                <button
                  onClick={() => setShowDateFilter(!showDateFilter)}
                  className={`flex items-center justify-center gap-2 px-4 py-3 border rounded-xl transition-all duration-200 w-full sm:w-auto ${
                    filters.dateFrom || filters.dateTo
                      ? "border-[#FF6C6C] bg-[#FF6C6C]/5 text-[#FF6C6C]"
                      : "border-gray-300 hover:border-[#FF6C6C] hover:bg-gray-50"
                  }`}
                >
                  <Calendar size={18} />
                  <span className="text-sm font-medium">
                    {filters.dateFrom || filters.dateTo
                      ? "Date Filtered"
                      : "Filter by Date"}
                  </span>
                  {(filters.dateFrom || filters.dateTo) && (
                    <X
                      size={16}
                      className="ml-1 hover:bg-[#FF6C6C]/10 rounded-full p-0.5"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDateFilterClear();
                      }}
                    />
                  )}
                </button>

                {/* Date Filter Dropdown */}
                {showDateFilter && (
                  <div className="absolute top-full left-0 right-0 sm:right-auto mt-2 w-full sm:w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-gray-900">
                        Filter by Date Range
                      </h3>
                      <button
                        onClick={() => setShowDateFilter(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          From Date
                        </label>
                        <input
                          type="date"
                          id="dateFrom"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] outline-none"
                          max={filters.dateTo || undefined}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          To Date
                        </label>
                        <input
                          type="date"
                          id="dateTo"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] outline-none"
                          min={filters.dateFrom || undefined}
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                      <button
                        onClick={handleDateFilterClear}
                        className="text-sm text-gray-600 hover:text-gray-800"
                      >
                        Clear
                      </button>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowDateFilter(false)}
                          className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => {
                            const dateFrom =
                              document.getElementById("dateFrom").value;
                            const dateTo =
                              document.getElementById("dateTo").value;
                            handleDateFilterApply(dateFrom, dateTo);
                          }}
                          className="px-4 py-2 text-sm text-white bg-[#FF6C6C] rounded-lg hover:bg-[#FF6C6C]/90"
                        >
                          Apply Filter
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Channel Filter */}
              <div className="relative w-full sm:w-auto">
                <div className="relative">
                  <select
                    value={filters.channel || ""}
                    onChange={(e) => updateChannelFilter(e.target.value)}
                    className={`w-full sm:w-auto px-4 py-3 pr-10 border rounded-xl focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-200 outline-none shadow-sm appearance-none ${
                      filters.channel
                        ? "border-[#FF6C6C] bg-[#FF6C6C]/5 text-[#FF6C6C]"
                        : "border-gray-300 hover:border-[#FF6C6C] hover:bg-gray-50"
                    }`}
                  >
                    <option value="">All Channels</option>
                    <option value="web">Web</option>
                    <option value="ussd">USSD</option>
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg
                      className="w-4 h-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                  {filters.channel && (
                    <button
                      onClick={() => updateChannelFilter("")}
                      className="absolute inset-y-0 right-6 flex items-center pr-2 hover:bg-[#FF6C6C]/10 rounded-r-lg"
                    >
                      <X
                        size={14}
                        className="text-[#FF6C6C] hover:text-[#FF6C6C]/80"
                      />
                    </button>
                  )}
                </div>
                {filters.channel && (
                  <div className="absolute top-full left-0 mt-1 w-full sm:w-auto bg-white border border-gray-200 rounded-lg shadow-lg z-10 p-2">
                    <div className="flex items-center gap-2 px-2 py-1">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          filters.channel === "web"
                            ? "bg-cyan-500"
                            : "bg-teal-500"
                        }`}
                      ></div>
                      <span className="text-xs font-medium text-gray-700">
                        Filtered by: {filters.channel.toUpperCase()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Items Per Page Selector */}
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <label className="text-sm text-gray-600 whitespace-nowrap">
                Show:
              </label>
              <select
                value={pagination?.itemsPerPage || 15}
                onChange={handleItemsPerPageChange}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] outline-none"
              >
                <option value={15}>15 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>

            {/* Export Button */}
            <div className="flex items-center space-x-3">
              <button
                onClick={handleExportClick}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] text-white rounded-lg hover:shadow-lg transition-all duration-300 hover:scale-105 font-semibold text-sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Debug Info - Remove this after confirming the fix */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs">
            {hasActiveFilters && (
              <p className="text-blue-600 mt-1">
                <strong>Active Filters:</strong>{" "}
                {filters.search ? `Search: "${filters.search}"` : ""}
                {filters.dateFrom || filters.dateTo
                  ? ` | Date: ${formatDateForDisplay(
                      filters.dateFrom
                    )} - ${formatDateForDisplay(filters.dateTo)}`
                  : ""}
                {filters.channel ? ` | Channel: ${filters.channel}` : ""}
              </p>
            )}
          </div>

          <div className="overflow-hidden bg-white border rounded-xl">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {/* <th className="px-3 sm:px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      <button
                        onClick={handleSelectAll}
                        className="text-gray-500 hover:text-[#FF6C6C]"
                      >
                        {selectedItems.length === subscribers.length &&
                        subscribers.length > 0 ? (
                          <CheckSquare className="w-4 h-4 sm:w-5 sm:h-5" />
                        ) : (
                          <Square className="w-4 h-4 sm:w-5 sm:h-5" />
                        )}
                      </button>
                    </th> */}
                    <th
                      onClick={() => requestSort("id")}
                      className="px-3 sm:px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        <span className="hidden sm:inline">ID</span>
                        <span className="sm:hidden">#</span>
                        {getSortIcon("id")}
                      </div>
                    </th>
                    <th
                      onClick={() => requestSort("full_name")}
                      className="px-3 sm:px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        <span className="hidden sm:inline">Full Name</span>
                        <span className="sm:hidden">Name</span>
                        {getSortIcon("full_name")}
                      </div>
                    </th>
                    <th
                      onClick={() => requestSort("email")}
                      className="hidden md:table-cell px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        Email {getSortIcon("email")}
                      </div>
                    </th>
                    <th
                      onClick={() => requestSort("phone_number")}
                      className="hidden lg:table-cell px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        <span className="hidden xl:inline">Phone</span>
                        <span className="xl:hidden">Phone #</span>
                        {getSortIcon("phone_number")}
                      </div>
                    </th>
                    <th
                      onClick={() => requestSort("location")}
                      className="hidden xl:table-cell px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        Location {getSortIcon("location")}
                      </div>
                    </th>
                    <th
                      onClick={() => requestSort("channel")}
                      className="hidden lg:table-cell px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        Channel {getSortIcon("channel")}
                      </div>
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                      <span className="hidden sm:inline">Action</span>
                      <span className="sm:hidden">...</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    Array.from({ length: pagination?.itemsPerPage || 15 }).map(
                      (_, index) => (
                        <tr key={`skeleton-${index}`} className="animate-pulse">
                          <td className="px-3 sm:px-6 py-4">
                            <div className="w-4 h-4 bg-gray-200 rounded"></div>
                          </td>
                          <td className="px-3 sm:px-6 py-4">
                            <div className="w-8 h-4 bg-gray-200 rounded"></div>
                          </td>
                          <td className="px-3 sm:px-6 py-4">
                            <div className="w-32 h-4 bg-gray-200 rounded"></div>
                          </td>
                          <td className="hidden md:table-cell px-6 py-4">
                            <div className="w-40 h-4 bg-gray-200 rounded"></div>
                          </td>
                          <td className="hidden lg:table-cell px-6 py-4">
                            <div className="w-24 h-4 bg-gray-200 rounded"></div>
                          </td>
                          <td className="hidden xl:table-cell px-6 py-4">
                            <div className="w-20 h-4 bg-gray-200 rounded"></div>
                          </td>
                          <td className="px-3 sm:px-6 py-4">
                            <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
                          </td>
                        </tr>
                      )
                    )
                  ) : subscribers.length === 0 ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="px-6 py-12 text-center text-gray-500"
                      >
                        <div className="flex flex-col items-center">
                          <Users className="w-12 h-12 text-gray-300 mb-4" />
                          <p className="text-lg font-medium">
                            No subscribers found
                          </p>
                          <p className="text-sm">
                            Try adjusting your search criteria
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    subscribers.map((item) => (
                      <tr
                        key={item.id}
                        className="transition-colors hover:bg-gray-50"
                      >
                        <td className="px-3 sm:px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {userId(item.id)}
                        </td>
                        <td className="px-3 sm:px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {item.full_name}
                            </span>
                            <span className="text-xs text-gray-500 md:hidden">
                              {item.email ? (
                                <div className="flex items-center mt-1 group">
                                  <div className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border border-green-200">
                                    <svg
                                      className="w-2.5 h-2.5 mr-1"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                    </svg>
                                    <span
                                      className="truncate max-w-[150px]"
                                      title={item.email}
                                    >
                                      {item.email}
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => {
                                      handleCopy(item.email, "email", item.id);
                                    }}
                                    className="ml-1 p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-green-100 text-green-600 hover:text-green-700"
                                    title="Copy email"
                                  >
                                    {copyAnimation.email === item.id ? (
                                      <svg
                                        className="w-3 h-3"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M5 13l4 4L19 7"
                                        />
                                      </svg>
                                    ) : (
                                      <svg
                                        className="w-3 h-3"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                        />
                                      </svg>
                                    )}
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center mt-1">
                                  <div className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gradient-to-r from-gray-50 to-slate-50 text-gray-500 border border-gray-200">
                                    <svg
                                      className="w-2.5 h-2.5 mr-1"
                                      fill="currentColor"
                                      viewBox="0 0 20 20"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                    <span>No email</span>
                                  </div>
                                </div>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="hidden md:table-cell px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                          {item.email ? (
                            <div className="flex items-center group">
                              <div className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border border-green-200">
                                <svg
                                  className="w-3 h-3 mr-1.5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                </svg>
                                <span
                                  className="truncate max-w-[150px]"
                                  title={item.email}
                                >
                                  {item.email}
                                </span>
                              </div>
                              <button
                                onClick={() => {
                                  handleCopy(item.email, "email", item.id);
                                }}
                                className="ml-2 p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-green-100 text-green-600 hover:text-green-700"
                                title="Copy email"
                              >
                                {copyAnimation.email === item.id ? (
                                  <svg
                                    className="w-3.5 h-3.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    className="w-3.5 h-3.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                    />
                                  </svg>
                                )}
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <div className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gradient-to-r from-gray-50 to-slate-50 text-gray-500 border border-gray-200">
                                <svg
                                  className="w-3 h-3 mr-1.5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span>No email</span>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="hidden lg:table-cell px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                          {item.phone_number ? (
                            <div className="flex items-center group">
                              <div className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gradient-to-r from-purple-50 to-violet-50 text-purple-700 border border-purple-200">
                                <svg
                                  className="w-3 h-3 mr-1.5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                </svg>
                                <span
                                  className="font-mono"
                                  title={item.phone_number}
                                >
                                  {item.phone_number}
                                </span>
                              </div>
                              <button
                                onClick={() => {
                                  handleCopy(
                                    item.phone_number,
                                    "phone",
                                    item.id
                                  );
                                }}
                                className="ml-2 p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-purple-100 text-purple-600 hover:text-purple-700"
                                title="Copy phone number"
                              >
                                {copyAnimation.phone === item.id ? (
                                  <svg
                                    className="w-3.5 h-3.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 13l4 4L19 7"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    className="w-3.5 h-3.5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                    />
                                  </svg>
                                )}
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <div className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gradient-to-r from-gray-50 to-slate-50 text-gray-500 border border-gray-200">
                                <svg
                                  className="w-3 h-3 mr-1.5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span>No phone</span>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="hidden xl:table-cell px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                          {item.location &&
                          item.location !== "Unknown" &&
                          item.location !== "unknown" ? (
                            <div className="flex items-center">
                              <div className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gradient-to-r from-orange-50 to-amber-50 text-orange-700 border border-orange-200">
                                <svg
                                  className="w-3 h-3 mr-1.5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span
                                  className="truncate max-w-[150px]"
                                  title={item.location}
                                >
                                  {item.location}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <div className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gradient-to-r from-gray-50 to-slate-50 text-gray-500 border border-gray-200">
                                <svg
                                  className="w-3 h-3 mr-1.5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span>Unknown</span>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="hidden lg:table-cell px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                          {item.channel ? (
                            <div className="flex items-center">
                              <div
                                className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border ${
                                  item.channel.toLowerCase() === "web"
                                    ? "bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-700 border-cyan-200"
                                    : item.channel.toLowerCase() === "ussd"
                                    ? "bg-gradient-to-r from-teal-50 to-green-50 text-teal-700 border-teal-200"
                                    : "bg-gradient-to-r from-gray-50 to-slate-50 text-gray-500 border-gray-200"
                                }`}
                              >
                                {item.channel.toLowerCase() === "web" ? (
                                  <svg
                                    className="w-3 h-3 mr-1.5"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                ) : item.channel.toLowerCase() === "ussd" ? (
                                  <svg
                                    className="w-3 h-3 mr-1.5"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                  </svg>
                                ) : (
                                  <svg
                                    className="w-3 h-3 mr-1.5"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                )}
                                <span className="font-medium uppercase">
                                  {item.channel}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <div className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gradient-to-r from-gray-50 to-slate-50 text-gray-500 border border-gray-200">
                                <svg
                                  className="w-3 h-3 mr-1.5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span>Unknown</span>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                          <div className="relative action-dropdown">
                            <button
                              className="p-1.5 rounded-full hover:bg-gray-100"
                              onClick={(e) => handleDropdownToggle(item.id, e)}
                            >
                              <MoreVertical size={16} />
                            </button>
                            {dropdownOpen === item.id && (
                              <div
                                className="fixed z-50 w-48 overflow-hidden bg-white border shadow-lg rounded-xl"
                                style={dropdownPosition}
                              >
                                <button
                                  onClick={() => {
                                    setSelectedBrand(item);
                                    setViewModal(true);
                                    setDropdownOpen(null);
                                  }}
                                  className="flex items-center w-full px-4 py-3 space-x-2 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                  <Eye size={14} className="text-orange-500" />
                                  <span>View Subscriber Info</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {(pagination?.totalPages || 1) > 1 && (
              <div className="px-3 sm:px-6 py-3 bg-gray-50">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm text-gray-500 text-center sm:text-left">
                    Showing{" "}
                    {((pagination?.currentPage || 1) - 1) *
                      (pagination?.itemsPerPage || 15) +
                      1}{" "}
                    to{" "}
                    {Math.min(
                      (pagination?.currentPage || 1) *
                        (pagination?.itemsPerPage || 15),
                      getDisplayTotal()
                    )}{" "}
                    of {getDisplayTotal().toLocaleString()} entries
                  </p>
                  <div className="flex items-center justify-center sm:justify-end space-x-1 sm:space-x-2">
                    <button
                      onClick={() =>
                        changePage((pagination?.currentPage || 1) - 1)
                      }
                      disabled={!(pagination?.hasPrev || false)}
                      className={`p-2 rounded ${
                        !(pagination?.hasPrev || false)
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      <ChevronLeft size={16} />
                    </button>

                    {/* Smart Pagination Numbers */}
                    <div className="hidden sm:flex items-center space-x-1">
                      {getPaginationNumbers().map((number, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            if (number !== "...") {
                              changePage(number);
                            }
                          }}
                          disabled={number === "..."}
                          className={`px-2 sm:px-3 py-1 rounded text-sm ${
                            number === "..."
                              ? "text-gray-400 cursor-default"
                              : (pagination?.currentPage || 1) === number
                              ? "bg-[#FF6C6C] text-white"
                              : "text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          {number}
                        </button>
                      ))}
                    </div>

                    {/* Mobile pagination - show current page and total */}
                    <div className="flex sm:hidden items-center space-x-2">
                      <span className="text-sm text-gray-600">
                        Page {pagination?.currentPage || 1} of{" "}
                        {pagination?.totalPages || 1}
                      </span>
                    </div>

                    <button
                      onClick={() =>
                        changePage((pagination?.currentPage || 1) + 1)
                      }
                      disabled={!(pagination?.hasNext || false)}
                      className={`p-2 rounded ${
                        !(pagination?.hasNext || false)
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      <ViewSubscribersModal
        isOpen={viewModal}
        onClose={() => setViewModal(false)}
        subscribers={selectedBrand}
      />
      <DeactivateModal
        isOpen={deactivateModal}
        onClose={() => setDeactivateModal(false)}
        userDetails={selectedBrand}
      />

      {/* Export Modal */}
      {exportModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
            >
              <div className="absolute inset-0 bg-gray-900/75 backdrop-blur-sm"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              {/* Header */}
              <div className="relative overflow-hidden bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] px-6 py-4">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -translate-y-8 translate-x-8"></div>

                <div className="relative flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm border border-white/30">
                      <Download className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white">
                        Export Subscribers
                      </h3>
                      <p className="text-white/90 text-sm">
                        Choose export options
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleExportCancel}
                    className="text-white/90 hover:text-white transition-colors p-2 rounded-xl hover:bg-white/10"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column - Export Type */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                      <Target className="w-4 h-4 mr-2 text-[#FF6C6C]" />
                      Export Type
                    </h4>
                    <div className="space-y-2">
                      {[
                        {
                          value: "all",
                          label: "All Subscribers",
                          description: "Complete database",
                        },
                        {
                          value: "ussd",
                          label: "USSD Users Only",
                          description: "USSD registrations",
                        },
                        {
                          value: "web",
                          label: "Web Users Only",
                          description: "Web registrations",
                        },
                      ].map((option) => (
                        <label
                          key={option.value}
                          className={`relative flex items-start p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md ${
                            exportFilters.exportType === option.value
                              ? "border-[#FF6C6C] bg-[#FF6C6C]/5"
                              : "border-gray-200 bg-white hover:border-gray-300"
                          }`}
                        >
                          <input
                            type="radio"
                            name="exportType"
                            value={option.value}
                            checked={exportFilters.exportType === option.value}
                            onChange={(e) =>
                              handleExportTypeChange(e.target.value)
                            }
                            className="sr-only"
                          />
                          <div
                            className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center mr-3 mt-0.5 ${
                              exportFilters.exportType === option.value
                                ? "border-[#FF6C6C] bg-[#FF6C6C]"
                                : "border-gray-300"
                            }`}
                          >
                            {exportFilters.exportType === option.value && (
                              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-gray-900">
                              {option.label}
                            </div>
                            <div className="text-xs text-gray-500">
                              {option.description}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Right Column - Date Filters & Summary */}
                  <div className="space-y-4">
                    {/* Date Filters */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                        <Calendar className="w-4 h-4 mr-2 text-[#FF6C6C]" />
                        Date Range (Optional)
                      </h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            From Date
                          </label>
                          <input
                            type="date"
                            value={exportFilters.dateFrom}
                            onChange={(e) =>
                              handleExportDateChange("dateFrom", e.target.value)
                            }
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-colors text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            To Date
                          </label>
                          <input
                            type="date"
                            value={exportFilters.dateTo}
                            onChange={(e) =>
                              handleExportDateChange("dateTo", e.target.value)
                            }
                            min={exportFilters.dateFrom}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-colors text-sm"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {exportFilters.exportType === "all"
                          ? "Leave empty for all subscribers"
                          : `Leave empty for all ${exportFilters.exportType.toUpperCase()} subscribers`}
                      </p>
                    </div>

                    {/* Export Summary */}
                    <div className="p-3 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-100">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                        <Info className="w-4 h-4 mr-2 text-[#FF6C6C]" />
                        Summary
                      </h4>
                      <div className="text-sm text-gray-600">
                        {exportFilters.exportType === "all" ? (
                          <div>
                            <p>
                              Exporting{" "}
                              <span className="font-semibold text-[#FF6C6C]">
                                all {subscriberStats.total?.toLocaleString()}
                              </span>{" "}
                              subscribers
                            </p>
                            {(exportFilters.dateFrom ||
                              exportFilters.dateTo) && (
                              <p className="mt-1 text-xs">
                                Date: {exportFilters.dateFrom || "Any"} to{" "}
                                {exportFilters.dateTo || "Any"}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div>
                            <p>
                              Exporting{" "}
                              <span className="font-semibold text-[#FF6C6C]">
                                {exportFilters.exportType.toUpperCase()}
                              </span>{" "}
                              subscribers
                            </p>
                            {exportFilters.dateFrom || exportFilters.dateTo ? (
                              <p className="mt-1 text-xs">
                                Date: {exportFilters.dateFrom || "Any"} to{" "}
                                {exportFilters.dateTo || "Any"}
                              </p>
                            ) : (
                              <p className="mt-1 text-xs text-gray-500">
                                All {exportFilters.exportType.toUpperCase()}{" "}
                                subscribers
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-t border-gray-100">
                <div className="flex items-center justify-between">
                  <button
                    onClick={handleExportCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleExportSubmit}
                    disabled={isExportDisabled() || exportLoading}
                    className={`px-6 py-2 bg-gradient-to-r from-[#FF6C6C] to-[#FF8F8F] text-white rounded-lg hover:shadow-lg transition-all duration-300 font-semibold text-sm flex items-center ${
                      isExportDisabled() || exportLoading
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:scale-105"
                    }`}
                  >
                    {exportLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Export Data
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscribers;
